import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import type { SlideGamePhase } from '@/types/slide'

interface RoundStatusBarProps {
	phase: SlideGamePhase
	bettingEndsAt: number | null
	bettingWindowSeconds: number
	animationEnded?: boolean
	className?: string
}

export function RoundStatusBar({ phase, bettingEndsAt, bettingWindowSeconds, animationEnded = false, className }: RoundStatusBarProps) {
	const [remainingMs, setRemainingMs] = useState(0)

	useEffect(() => {
		if (phase === 'waiting' && bettingEndsAt) {
			const tick = () => {
				setRemainingMs(Math.max(0, bettingEndsAt - Date.now()))
			}

			tick()
			const interval = window.setInterval(tick, 50)
			return () => window.clearInterval(interval)
		}

		// Hide while the strip is still spinning / landing.
		if (phase === 'playing' || (phase === 'result' && !animationEnded)) {
			setRemainingMs(-1)
			return undefined
		}

		// After the land finishes (and other idle gaps), show Starting…
		setRemainingMs(0)
		return undefined
	}, [animationEnded, bettingEndsAt, phase])

	const windowMs = Math.max(bettingWindowSeconds * 1000, 1)
	const progress = remainingMs > 0 ? (remainingMs / windowMs) * 100 : 0

	if (remainingMs < 0) {
		return null
	}

	return (
		<div className={cn('w-full', className)}>
			{remainingMs === 0 ? <p className='px-4 pb-3 text-center text-xs font-medium text-muted-foreground'>Starting…</p> : null}
			{remainingMs > 0 ? (
				<div className='h-1 w-full bg-secondary/50'>
					<div
						className='h-full bg-warning transition-[width] duration-75'
						style={{
							width: `${progress}%`,
							boxShadow: `rgba(255, 176, 25, 0.4) 0px 0px 10px,
              rgba(255, 255, 255, 0.2) 0px 1px 0px inset,
              rgba(0, 0, 0, 0.15) 0px -3px 0px inset, rgb(255, 135, 25) 0px 0px 9px inset`,
						}}
					/>
				</div>
			) : null}
		</div>
	)
}
